import { useEffect, useRef, useState } from "react"
import { ZoomIn, ZoomOut, Download } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useMobileDeviceClasses } from "@lsproule/mobile-detect"

export interface PDFViewerProps {
  pdfUrl: string
  fileName?: string
}


export function PDFViewer({ pdfUrl, fileName = "document.pdf" }: PDFViewerProps) {
  useMobileDeviceClasses()

  const root = document.documentElement
  root.classList.add("disable-scroll")
  const mobile = root.classList.contains("mobile-device")
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map())
  const renderingRef = useRef<Set<number>>(new Set())
  const pdfDocRef = useRef<any>(null)
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(mobile ? .5 : 1)
  const [error, setError] = useState<string | null>(null)
  const [pdfLibLoaded, setPdfLibLoaded] = useState(false)
  const [pageInput, setPageInput] = useState("1")
  const [containerWidth, setContainerWidth] = useState(0)

  // Load PDF.js library
  useEffect(() => {
    if ((window as any).pdfjsLib) {
      setPdfLibLoaded(true)
      return
    }
    const script = document.createElement("script")
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"
    script.async = true
    script.onload = () => {
      const lib = (window as any).pdfjsLib
      lib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"
      setPdfLibLoaded(true)
    }
    script.onerror = () => setError("Failed to load PDF library")
    document.head.appendChild(script)
  }, [])
  // Load PDF
  useEffect(() => {
    if (!pdfLibLoaded) return

    const loadPdf = async () => {
      try {
    const url = `${window.location.origin}/fax/documents/${pdfUrl}`
        const pdf = await (window as any).pdfjsLib.getDocument(
      {
        url: url
      }
    ).promise
        pdfDocRef.current = pdf
        setNumPages(pdf.numPages)
        setPageInput("1")
      } catch (err: any) {
        setError(err.message || "Failed to load PDF")
      }
    }

    loadPdf()
  }, [pdfLibLoaded])

  // Measure container
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Calculate responsive scale

  // Calculate responsive scale
  const responsiveScale = containerWidth ? Math.max(0.5, Math.min(scale, (containerWidth - 32) / 612)) : scale

  // Render page
  const renderPage = async (pageNum: number) => {
    if (!pdfDocRef.current || renderingRef.current.has(pageNum)) return
    const canvas = canvasRefs.current.get(pageNum)
    if (!canvas) return

    renderingRef.current.add(pageNum)
    try {
      const page = await pdfDocRef.current.getPage(pageNum)
      const viewport = page.getViewport({ scale })
      const dpr = window.devicePixelRatio || 1
      canvas.width = viewport.width * dpr
      canvas.height = viewport.height * dpr
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`
      const ctx = canvas.getContext("2d")
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        await page.render({ canvasContext: ctx, viewport }).promise
      }
    } finally {
      renderingRef.current.delete(pageNum)
    }
  }

  // Render all visible pages
  useEffect(() => {
    if (!containerRef.current) return
    const canvases = Array.from(canvasRefs.current.keys())
    canvases.forEach(renderPage)
  }, [numPages, scale])

  // Handle page jump
  const handlePageJump = () => {
    const pageNum = Math.min(Math.max(Number(pageInput || 1), 1), numPages)
    const canvas = canvasRefs.current.get(pageNum)
    if (canvas && containerRef.current) {
      canvas.scrollIntoView({ behavior: "smooth", block: "start" })
    }
    setPageInput(String(pageNum))
  }

  return (
    <div className="h-screen overflow-y-none w-full flex flex-col ">
      {/* Fixed Toolbar */}
      <div className="flex items-center justify-between gap-2 border-b p-3 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={numPages}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePageJump()}
            className="w-16 px-2 py-1 border rounded"
          />
          <span className="text-sm">of {numPages}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setScale(s => Math.max(0.5, s - 0.2))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm">{Math.round(responsiveScale * 100)}%</span>
          <Button
            onClick={() => setScale(s => Math.min(3, s + 0.2))}
            className="p-2 border rounded "
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            onClick={async () => {
              try {
                const response = await fetch(`${window.location.origin}/fax/documents/${pdfUrl}`)
                if (!response.ok) throw new Error('Download failed')
                
                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
              } catch (error) {
                console.error('Download failed:', error)
              }
            }}
            className="p-2 border rounded "
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable PDF container */}
      <div ref={containerRef} className="flex-1 mb-5 overflow-y-scroll auto flex flex-col items-center gap-4 p-4">
        {error ? (
          <div className="text-red-600 text-center mt-10">{error}</div>
        ) : (
          Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
            <canvas
              key={pageNum}
              ref={(el) => {
                if (el) canvasRefs.current.set(pageNum, el)
                else canvasRefs.current.delete(pageNum)
              }}
              className="shadow-lg border"
            />
          ))
        )}
      </div>
    </div>
  )
}
//interface PDFViewerProps {
//  pdfUrl: string
//  fileName?: string
//}
//
//export function PDFViewer({ pdfUrl, fileName = "document.pdf" }: PDFViewerProps) {
//  const host = window.location.origin
//
//  return (
//    <div className="flex h-full flex-col">
//      <div className="flex-1 overflow-auto bg-muted/30">
//        <iframe src={`${host}/fax/documents/${pdfUrl}`}  className="h-full w-full border-0" title={fileName} />
//      </div>
//    </div>
//  )
//}

